import React, { useCallback, useEffect, useRef, useState } from 'react';

export interface DraggableWrapperProps {
  children: React.ReactNode;
  /** Initial position or controlled position */
  position?: { x: number; y: number };
  /** DOM element ref to constrain drag area (window size if not provided) */
  boundsRef?: HTMLElement | null;
  /** Drag handle class (only elements with this class can initiate drag) */
  handleClass?: string;
  /** List of tags that are not draggable */
  disabledTags?: string[];
  /** List of parent classes that are not draggable */
  disabledParentClasses?: string[];
  /** Callback when drag ends */
  onDragEnd?: (position: { x: number; y: number }) => void;
  /** Callback when drag starts */
  onDragStart?: (e: MouseEvent) => void;
  /** Custom style */
  style?: React.CSSProperties;
  /** Position control type (fixed or relative) */
  positionType?: 'fixed' | 'relative';
  /** Ref to pass to inner element */
  innerRef?: React.RefObject<HTMLDivElement | null>;
}

/**
 * Reusable draggable wrapper component
 *
 * Features:
 * - Constrain drag area via boundsRef (RefObject to a DOM element)
 * - Specify drag handle with handleClass (only elements with this class can initiate drag)
 * - Disable drag on specific elements with disabledTags/disabledParentClasses
 * - Pure React event handling for fast responsiveness
 *
 * Usage:
 * 1. Fixed position (e.g., floating toolbar):
 *    <DraggableWrapper
 *      position={position}
 *      boundsRef={containerRef}  // Optional: constrains drag to this element
 *      handleClass="drag-handle"
 *      onDragEnd={setPosition}
 *    >
 *      <YourComponent />
 *    </DraggableWrapper>
 *
 * 2. Relative position with transform (e.g., modal dialog):
 *    <DraggableWrapper
 *      position={position}
 *      positionType="relative"
 *      onDragEnd={setPosition}
 *    >
 *      {modal}
 *    </DraggableWrapper>
 *
 * Notes:
 * - If boundsRef is not provided, bounds are calculated based on window size
 * - For positionType="relative", bounds are calculated dynamically based on
 *   the element's current position (transform-based)
 * - Bounds are recalculated on each drag move, so they adapt to container resizing
 * - For resize event handling, use the parent context (e.g., GlobalShapesContext)
 *   to reposition the element within new bounds
 */
const DraggableWrapper: React.FC<DraggableWrapperProps> = ({
  children,
  position = { x: 0, y: 0 },
  boundsRef,
  handleClass,
  disabledTags = [],
  disabledParentClasses = [],
  onDragEnd,
  onDragStart,
  style = {},
  positionType = 'fixed',
  innerRef,
}) => {
  const defaultRef = useRef<HTMLDivElement>(null);
  const elementRef = innerRef || defaultRef;

  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [currentPosition, setCurrentPosition] = useState(position);

  // Update position when it changes from outside
  useEffect(() => {
    setCurrentPosition(position);
  }, [position]);

  // boundsRef or window size based bounds calculation
  const calculateBounds = useCallback(() => {
    const toolbarWidth = elementRef.current?.children?.[0]?.clientWidth || elementRef.current?.offsetWidth || 350;
    const toolbarHeight = elementRef.current?.children?.[0]?.clientHeight || elementRef.current?.offsetHeight || 100;

    if (boundsRef) {
      const rect = boundsRef.getBoundingClientRect();
      return {
        top: rect.top,
        left: rect.left,
        right: rect.right - toolbarWidth,
        bottom: rect.bottom - toolbarHeight,
      };
    }

    // boundsRef is not provided, window size based bounds calculation
    if (positionType === 'relative' && elementRef.current) {
      // relative(transform based) bounds calculation based on current position
      const targetRect = elementRef.current.getBoundingClientRect();
      const { clientWidth, clientHeight } = window.document.documentElement;
      return {
        left: -targetRect.left + currentPosition.x,
        right: clientWidth - targetRect.right + currentPosition.x,
        top: -targetRect.top + currentPosition.y,
        bottom: clientHeight - targetRect.bottom + currentPosition.y,
      };
    }

    // fixed mode, window size based bounds calculation
    return {
      top: 0,
      left: 0,
      right: window.innerWidth - toolbarWidth,
      bottom: window.innerHeight - toolbarHeight,
    };
  }, [boundsRef, elementRef, positionType, currentPosition]);

  const checkDisabled = (target: HTMLElement): boolean => {
    // tag check
    if (disabledTags.includes(target.tagName)) {
      return true;
    }

    // parent class check
    if (disabledParentClasses.some(cls => target.closest(cls))) {
      return true;
    }

    return false;
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const target = e.target as HTMLElement;

    // disabled tag check
    if (checkDisabled(target)) {
      return;
    }

    // handle class check
    if (handleClass && !target.closest(`.${handleClass}`)) {
      return;
    }

    setIsDragging(true);
    setDragOffset({
      x: e.clientX - currentPosition.x,
      y: e.clientY - currentPosition.y,
    });

    if (onDragStart) {
      onDragStart(e.nativeEvent);
    }

    e.preventDefault();
  };

  useEffect(() => {
    if (!isDragging) return undefined;

    const handleMouseMove = (e: MouseEvent) => {
      let newX = e.clientX - dragOffset.x;
      let newY = e.clientY - dragOffset.y;

      // bounds application
      const bounds = calculateBounds();
      newX = Math.max(bounds.left, Math.min(newX, bounds.right));
      newY = Math.max(bounds.top, Math.min(newY, bounds.bottom));

      setCurrentPosition({ x: newX, y: newY });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      if (onDragEnd) {
        onDragEnd(currentPosition);
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset, currentPosition, calculateBounds, onDragEnd]);

  // position type based style application
  const positionStyle =
    positionType === 'fixed'
      ? {
          left: currentPosition.x,
          top: currentPosition.y,
        }
      : {
          // relative mode, transform usage (AntD Modal center alignment maintenance)
          transform: `translate(${currentPosition.x}px, ${currentPosition.y}px)`,
        };

  return (
    <div
      ref={elementRef}
      onMouseDown={handleMouseDown}
      style={{
        position: positionType,
        ...positionStyle,
        cursor: isDragging ? 'grabbing' : 'default',
        ...style,
      }}
    >
      {children}
    </div>
  );
};

export default DraggableWrapper;
